import { FC, Fragment, useEffect, useState } from "react";
import { Box, Typography, TextField, Grid, Autocomplete } from "@mui/material";
import styles from "./ProfileCard.module.scss";
import generalStyles from "styles/generalStyles.module.scss";
import { useAppSelector } from "utils/hooks/storeHooks";
import { selectSessionCredentials } from "store/session/selectors";
import { useFormikContext } from "formik";
import { ProfileDefaultFilters, ProfileFormValues } from "types/Profile";

import { useDebounce } from "utils/hooks/useDebounce";
import { SelectOption } from "types";
import { getSelectOptions } from "utils/helpers/selectOptionsHelper";

type ProfileErrors = {
  firstName?: string;
  lastName?: string;
};
type ProfileCardProps = {
  title: string;
  isEditing?: boolean;
  errors?: ProfileErrors;
  filtersData?: ProfileDefaultFilters;
};
type cardData = { subtitle: string; text: string; name: string };

const ProfileCard: FC<ProfileCardProps> = ({
  title,
  isEditing,
  errors,
  filtersData,
}: ProfileCardProps) => {
  const isDefault: boolean = title === "Default Filters Configuration";
  let cardInfo: cardData[] = [];
  const nonEditable = ["email", "payer", "contract", "location"];
  const credentials = useAppSelector(selectSessionCredentials);
  const { profile: myProfile, user } = credentials;
  const [taxonomySelectOptions, setTaxonomySelectOptions] = useState<
    SelectOption[]
  >([]);
  const [taxonomy, setTaxonomy] = useState<string>("");
  const [debouncedTaxonomy] = useDebounce(taxonomy, 250);

  useEffect(() => {
    if (filtersData !== undefined) {
      if (debouncedTaxonomy === "") {
        const taxonomySelectOptions = getSelectOptions(
          filtersData["taxonomy"]!.slice(0, 5)
        );
        setTaxonomySelectOptions(taxonomySelectOptions);
      } else if (debouncedTaxonomy.length > 0) {
        const taxonomySelectOptions = getSelectOptions(
          filtersData["taxonomy"]!.filter((item: any) =>
            item.toLowerCase().includes(debouncedTaxonomy.toLowerCase())
          ).slice(0, 5)
        );
        setTaxonomySelectOptions(taxonomySelectOptions);
      }
    }
  }, [filtersData, debouncedTaxonomy]);

  const handleSearchTaxonomy = (_e: React.SyntheticEvent, taxonomy: string) => {
    setTaxonomy(taxonomy);
  };

  const getDefaultFilterText = (filter: string) => {
    if (myProfile[filter] !== null && myProfile[filter].length > 0) {
      return myProfile[filter].join(", ");
    }
    return "";
  };

  const addressData = [
    {
      subtitle: "Country",
      text: `${myProfile.country ?? ""}`,
      name: "country",
    },
    { subtitle: "City / State", text: `${myProfile.city ?? ""}`, name: "city" },
    {
      subtitle: "Postal Code",
      text: `${myProfile.postalCode ?? ""}`,
      name: "postalCode",
    },
  ];

  const personalData = [
    {
      subtitle: "First Name",
      text: `${user.name.split(" ")[0]}`,
      name: "firstName",
    },
    {
      subtitle: "Last Name",
      text: `${user.name.split(" ")[1]}`,
      name: "lastName",
    },
    { subtitle: "Email Address", text: `${user.email}`, name: "email" },
    {
      subtitle: "Phone Number",
      text: `${myProfile.phoneNumber ?? ""}`,
      name: "phoneNumber",
    },
  ];
  const defaultData = [
    {
      subtitle: "Payer",
      text: "Unicare Community Health Center LA",
      name: "payer",
    },
    {
      subtitle: "Taxonomy",
      text: getDefaultFilterText("taxonomy"),
      name: "taxonomy",
    },
    { subtitle: "Location", text: "Los Angeles, California", name: "location" },
    { subtitle: "Contract", text: "", name: "contract" },
  ];

  switch (title) {
    case "Personal Information":
      cardInfo = [...personalData];
      break;
    case "Address":
      cardInfo = [...addressData];
      break;
    case "Default Filters Configuration":
      cardInfo = [...defaultData];
      break;
    default:
      break;
  }

  const { values, handleChange, setFieldValue } =
    useFormikContext<ProfileFormValues>();

  const cardContent = cardInfo.map((data, i) => (
    <Grid item xs={6} key={i}>
      <Box display="flex" flexDirection="column">
        <Typography
          color="#787878"
          variant="caption"
          sx={{ gridArea: `subtitle-${i + 1}` }}
        >
          {data.subtitle}
        </Typography>
        {isEditing && !nonEditable.includes(data.name) ? (
          isDefault ? (
            <Autocomplete
              sx={{ width: "100%" }}
              disableCloseOnSelect
              value={values.taxonomy}
              options={taxonomySelectOptions}
              filterOptions={(x) => x}
              multiple
              openOnFocus
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="taxonomy"
                  value={
                    values.taxonomy !== undefined && values.taxonomy.length > 0
                      ? values.taxonomy
                      : ""
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
              onInputChange={handleSearchTaxonomy}
              onChange={(_, value) => {
                setFieldValue("taxonomy", value);
              }}
            />
          ) : (
            <Fragment>
              <TextField
                name={data.name}
                value={values[data.name as keyof ProfileFormValues]}
                onChange={handleChange}
              />
              {errors !== undefined &&
                errors[data.name as keyof ProfileErrors] && (
                  <div className={generalStyles.formError}>
                    {errors[data.name as keyof ProfileErrors]}
                  </div>
                )}
            </Fragment>
          )
        ) : (
          <Typography
            variant="caption"
            mb="1.5rem"
            sx={{ gridArea: `text-${i + 1}` }}
          >
            {data.text === "" ? "---" : data.text}
          </Typography>
        )}
      </Box>
    </Grid>
  ));

  return (
    <Box className={styles.profileInfoCard}>
      <Box
        sx={
          isDefault
            ? { gridTemplateColumns: "23rem 23rem 1fr" }
            : {
                gridTemplateColumns: "15rem minmax(13rem, 20rem)",
              }
        }
      >
        <Typography
          variant="h3"
          display="inline"
          mb="1rem"
          sx={{ gridArea: "title" }}
        >
          {title}
        </Typography>
        <Grid container spacing={2}>
          {cardContent}
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfileCard;
